import { useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import Divider from '@mui/material/Divider';
import { LoadingState, PageSection, useConfirm, useToast } from '@radiology/ui';
import { useCertificateNames } from './api';
import {
  useCertificateLists,
  useDeleteList,
  useDeleteListItem,
  useMapTitle,
  useSaveList,
  useSaveListItem,
  useUnmapTitle,
} from './lists';

/**
 * Certificate lists — CERT-09 and CERT-10, inside the existing Lists area.
 *
 * The CR asks for this to extend System settings rather than become a page of
 * its own, so this is a panel the settings screen renders when the selected
 * module is Certifications. It is not routed and has no nav entry.
 *
 * Four things are configured here, which are the CR's recommended fields:
 *
 *   Certificate List Name   the list itself, e.g. General or Nursing
 *   Certificate Type        the certificates on it
 *   Mapped Title            which employee Titles it applies to (CERT-10)
 *   Active                  per type — retires one without deleting history
 *
 * The actual clinical certificate names are out of scope for the CR and are not
 * seeded here. General starts from the existing certificate vocabulary and the
 * business builds the rest.
 */
export function CertificateListsPanel({
  editable,
  titles,
}: {
  editable: boolean;
  /** The Title vocabulary, from the same attributes table the profile form uses. */
  titles: string[];
}) {
  const { lists, items, titles: mapped, isLoading, error } = useCertificateLists();
  const names = useCertificateNames();
  const saveList = useSaveList();
  const deleteList = useDeleteList();
  const saveItem = useSaveListItem();
  const deleteItem = useDeleteListItem();
  const mapTitle = useMapTitle();
  const unmapTitle = useUnmapTitle();
  const confirm = useConfirm();
  const toast = useToast();

  const [newList, setNewList] = useState('');
  const [newListAllTitles, setNewListAllTitles] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fail = (e: Error) => setFormError(e.message);
  const ok = (message: string) => () => {
    setFormError(null);
    toast.success(message);
  };

  const byList = useMemo(() => {
    return lists.map(list => ({
      ...list,
      items: items.filter(i => i.list_name === list.name),
      titles: mapped.filter(t => t.list_name === list.name).map(t => t.title),
    }));
  }, [lists, items, mapped]);

  /**
   * Titles with no list of their own.
   *
   * Worth surfacing: an employee whose Title is mapped nowhere is measured
   * against the All Titles lists alone, which is a silent under-requirement
   * rather than an error, and nothing else on this screen would reveal it.
   */
  const unmappedTitles = useMemo(() => {
    const covered = new Set(mapped.map(t => t.title.trim().toLowerCase()));
    return titles.filter(t => t.trim() && !covered.has(t.trim().toLowerCase()));
  }, [titles, mapped]);

  const addList = () => {
    const name = newList.trim();
    if (!name) {
      setFormError('Give the list a name.');
      return;
    }
    if (lists.some(l => l.name.toLowerCase() === name.toLowerCase())) {
      setFormError(`There is already a list called “${name}”.`);
      return;
    }
    saveList.mutate(
      { name, all_titles: newListAllTitles, active: true },
      {
        onSuccess: () => {
          ok('List added')();
          setNewList('');
          setNewListAllTitles(false);
        },
        onError: fail,
      },
    );
  };

  const removeList = async (name: string) => {
    const list = byList.find(l => l.name === name);
    const confirmed = await confirm({
      title: `Remove the ${name} list?`,
      message:
        `Its ${list?.items.length ?? 0} certificate ${
          list?.items.length === 1 ? 'type stops' : 'types stop'
        } being required, and any title mapped to it loses them. ` +
        'No certificate is deleted — anything employees have already uploaded stays on their record.',
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!confirmed) return;
    deleteList.mutate(name, { onSuccess: ok('List removed'), onError: fail });
  };

  if (isLoading) return <LoadingState variant="skeleton" rows={5} />;

  return (
    <Stack gap={2.5}>
      <PageSection
        title="Certificate lists"
        description="Which certificates each employee is required to hold. An employee is measured against the All Titles lists plus the list mapped to their own title."
      >
        <Stack gap={2}>
          {error && <Alert severity="error">{error.message}</Alert>}
          {formError && <Alert severity="error">{formError}</Alert>}

          {!editable && (
            <Alert severity="info">
              You do not administer Certifications, so these are read-only.
            </Alert>
          )}

          {editable && unmappedTitles.length > 0 && (
            <Alert severity="warning">
              {unmappedTitles.length === 1 ? 'This title is' : 'These titles are'} not mapped to any
              list, so {unmappedTitles.length === 1 ? 'it only needs' : 'they only need'} the All
              Titles certificates: {unmappedTitles.join(', ')}.
            </Alert>
          )}

          {editable && (
            <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
              <TextField
                label="New list"
                placeholder="Nursing, Radiography…"
                value={newList}
                onChange={e => setNewList(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newListAllTitles}
                    onChange={e => setNewListAllTitles(e.target.checked)}
                  />
                }
                label="Applies to all titles"
              />
              <Button variant="contained" onClick={addList} disabled={saveList.isPending}>
                Add list
              </Button>
            </Stack>
          )}
        </Stack>
      </PageSection>

      {byList.map(list => (
        <PageSection
          key={list.name}
          title={list.name}
          description={
            list.all_titles
              ? 'Required of every employee, whatever their title.'
              : list.titles.length > 0
                ? `Required of: ${list.titles.join(', ')}`
                : 'Not mapped to any title yet, so nobody is required to hold these.'
          }
          actions={
            editable ? (
              <Stack direction="row" gap={1} alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={list.active}
                      onChange={e =>
                        saveList.mutate(
                          { ...list, active: e.target.checked },
                          { onSuccess: ok('Saved'), onError: fail },
                        )
                      }
                    />
                  }
                  label={<Typography variant="caption">Active</Typography>}
                />
                <Button size="small" color="error" onClick={() => void removeList(list.name)}>
                  Remove
                </Button>
              </Stack>
            ) : undefined
          }
        >
          <Stack gap={2}>
            {!list.active && (
              <Alert severity="info">
                This list is inactive, so none of its certificates are currently required.
              </Alert>
            )}

            {/* --- Certificate types ------------------------------------- */}
            <Stack gap={1}>
              <Typography variant="body2" color="text.secondary">
                Certificates on this list
              </Typography>
              {list.items.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  None yet.
                </Typography>
              ) : (
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  {list.items.map(item => (
                    <Chip
                      key={item.certificate_type}
                      size="small"
                      label={item.certificate_type}
                      // An inactive type is shown, not hidden. Hiding it would
                      // make "why is nobody being asked for this?" unanswerable
                      // from this screen.
                      variant={item.active ? 'filled' : 'outlined'}
                      color={item.active ? 'default' : undefined}
                      sx={item.active ? undefined : { opacity: 0.6 }}
                      onClick={
                        editable
                          ? () =>
                              saveItem.mutate(
                                { ...item, active: !item.active },
                                {
                                  onSuccess: ok(item.active ? 'Deactivated' : 'Activated'),
                                  onError: fail,
                                },
                              )
                          : undefined
                      }
                      onDelete={
                        editable
                          ? () =>
                              deleteItem.mutate(
                                {
                                  listName: list.name,
                                  certificateType: item.certificate_type,
                                },
                                { onSuccess: ok('Removed'), onError: fail },
                              )
                          : undefined
                      }
                    />
                  ))}
                </Stack>
              )}
              {editable && list.items.some(i => !i.active) && (
                <Typography variant="caption" color="text.secondary">
                  Faded certificates are inactive — not required and not offered for upload, but
                  every certificate already held is kept. Click one to reactivate it.
                </Typography>
              )}

              {editable && (
                <Autocomplete
                  freeSolo
                  options={(names.data ?? []).filter(
                    n => !list.items.some(i => i.certificate_type === n),
                  )}
                  value={null}
                  blurOnSelect
                  clearOnBlur
                  onChange={(_e, value) => {
                    const type = (typeof value === 'string' ? value : '').trim();
                    if (!type) return;
                    saveItem.mutate(
                      { list_name: list.name, certificate_type: type, active: true },
                      { onSuccess: ok('Added'), onError: fail },
                    );
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      label="Add a certificate"
                      sx={{ maxWidth: 320 }}
                    />
                  )}
                />
              )}
            </Stack>

            {/* --- Mapped titles (CERT-10) ------------------------------- */}
            {!list.all_titles && (
              <>
                <Divider />
                <Stack gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Titles this list applies to
                  </Typography>
                  {list.titles.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      None yet.
                    </Typography>
                  ) : (
                    <Stack direction="row" gap={0.75} flexWrap="wrap">
                      {list.titles.map(title => (
                        <Chip
                          key={title}
                          size="small"
                          label={title}
                          onDelete={
                            editable
                              ? () =>
                                  unmapTitle.mutate(
                                    { list_name: list.name, title },
                                    { onSuccess: ok('Unmapped'), onError: fail },
                                  )
                              : undefined
                          }
                        />
                      ))}
                    </Stack>
                  )}

                  {editable && (
                    <TextField
                      select
                      size="small"
                      label="Map a title"
                      value=""
                      onChange={e =>
                        mapTitle.mutate(
                          { list_name: list.name, title: e.target.value },
                          { onSuccess: ok('Mapped'), onError: fail },
                        )
                      }
                      sx={{ maxWidth: 320 }}
                      helperText="Employees with this title are required to hold everything on this list."
                    >
                      {titles
                        .filter(t => !list.titles.includes(t))
                        .map(t => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </PageSection>
      ))}
    </Stack>
  );
}
